import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Gauge, Palette, Search, Smartphone } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { FadeUp, SectionHead } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PROJECTS } from "@/data/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const PILLARS = [
  {
    icon: Palette,
    title: "Un design personnalisé",
    text: "Un template générique donne à votre entreprise le même visage que des milliers d'autres. Un design sur mesure raconte votre activité, votre univers et vos valeurs — c'est ce qui donne envie de vous faire confiance.",
  },
  {
    icon: Smartphone,
    title: "Un site responsive",
    text: "Plus de la moitié des visites se font sur téléphone. Chaque site que nous créons s'adapte parfaitement au mobile, à la tablette et à l'ordinateur, sans rien perdre en lisibilité ni en élégance.",
  },
  {
    icon: Gauge,
    title: "La vitesse de chargement",
    text: "Un site lent fait fuir les visiteurs et pénalise votre position sur Google. Nous optimisons les images, le code et le chargement pour que chaque page s'affiche rapidement.",
  },
  {
    icon: Search,
    title: "Le référencement naturel",
    text: "Un beau site invisible ne sert à rien. Structure technique propre, balises, contenu optimisé et mots-clés ciblés : chaque création part avec une base solide pour être visible sur Google.",
  },
];

const FEATURES = [
  "Prise de rendez-vous en ligne",
  "Réservation de table ou de trajet",
  "Formulaire de contact et demande de devis",
  "Galerie photos et avant/après",
  "Présentation des prestations et tarifs",
  "Avis clients",
  "Version bilingue",
  "Calendrier de disponibilités",
  "Carte et informations pratiques",
];

const STEPS = [
  { num: "01", text: "Vous nous expliquez votre projet en quelques mots, par formulaire ou par email." },
  { num: "02", text: "Nous créons gratuitement un aperçu de votre futur site internet, adapté à votre activité." },
  { num: "03", text: "Vous découvrez le résultat avant de décider. Si ça vous plaît, on continue — sinon, vous ne devez rien." },
  { num: "04", text: "Nous développons le site complet : design final, contenus, performance et référencement." },
  { num: "05", text: "Votre site est mis en ligne, hébergé et entretenu. Les modifications sont incluses dans l'abonnement." },
];

const FAQ_SEO = [
  {
    q: "Combien coûte la création d'un site internet ?",
    a: "Chez Les Amis du Web, la création d'un site internet sur mesure coûte 850 € (paiement unique), puis 65 € par mois pour l'hébergement, l'entretien et les modifications. Pas de coût caché : vous connaissez le budget exact avant de commencer.",
  },
  {
    q: "Combien de temps faut-il pour créer un site internet ?",
    a: "L'aperçu gratuit est généralement prêt en quelques jours. Une fois validé, le site complet est développé en 2 à 4 semaines selon la complexité du projet : nombre de pages, fonctionnalités et contenus à intégrer.",
  },
  {
    q: "Quelle est la différence entre un site vitrine et un site sur mesure ?",
    a: "Un site vitrine présente votre activité, vos prestations et vos coordonnées. Il peut être construit à partir d'un modèle générique — ou entièrement sur mesure. Chez nous, même un site vitrine est conçu sur mesure : design unique, contenus adaptés à votre métier et fonctionnalités choisies pour vos clients.",
  },
  {
    q: "Pourquoi créer un site internet professionnel ?",
    a: "Vos futurs clients vous cherchent sur Google avant de vous contacter. Un site internet professionnel vous rend visible 24h/24, inspire confiance, présente vos services et transforme les visiteurs en demandes de contact ou de rendez-vous.",
  },
  {
    q: "Est-ce que le site sera adapté aux téléphones ?",
    a: "Oui, systématiquement. Chaque site que nous créons est responsive : il s'adapte automatiquement au mobile, à la tablette et à l'ordinateur. C'est indispensable, car la majorité de vos visiteurs navigueront depuis leur téléphone.",
  },
  {
    q: "Proposez-vous un aperçu gratuit avant de commencer ?",
    a: "Oui, c'est notre grande différence. Nous créons gratuitement un premier aperçu de votre futur site internet, personnalisé pour votre activité, sans engagement et sans paiement. Vous voyez le résultat avant de décider.",
  },
  {
    q: "Est-ce que vous vous occupez du référencement Google ?",
    a: "Oui. Chaque site est construit avec une base technique solide pour le référencement naturel : structure propre, vitesse, responsive, balises et contenu optimisé autour de vos mots-clés. Le SEO prend du temps et personne ne peut garantir une première place — mais votre site part avec toutes les bonnes fondations.",
  },
];

export default function CreationSiteInternet() {
  usePageMeta({
    title: "Création site internet : guide complet, prix et délais | Les Amis du Web",
    description:
      "Tout comprendre sur la création d'un site internet : prix (850 € + 65 €/mois), délais, site vitrine ou sur mesure, responsive, référencement. Aperçu gratuit de votre futur site, sans engagement.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_SEO.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="creation-site-internet-page">
      <Header />
      <main className="relative pt-40 pb-24 overflow-hidden">
        <div className="glow-orb w-[520px] h-[520px] bg-blue-600/14 -top-32 -left-40" />
        <div className="glow-orb w-[420px] h-[420px] bg-violet-600/12 top-[30%] -right-40" />

        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <FadeUp>
            <p className="section-label mb-5">— Création site internet</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="pillar-title">
              La création de site internet, <span className="text-gradient">expliquée simplement.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="text-[var(--text-secondary)] text-base md:text-lg mt-6 leading-relaxed">
              Vous envisagez la création d'un site internet pour votre activité ? Cette page répond aux questions que
              se posent tous les professionnels : à quoi sert un site web, combien ça coûte, combien de temps ça
              prend, et comment se déroule un projet avec Les Amis du Web — de l'idée jusqu'à la mise en ligne.
            </p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/demande" className="btn-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white" data-testid="pillar-cta-top">
                Demander mon aperçu gratuit <ArrowRight size={15} />
              </Link>
              <Link to="/#realisations" className="glass inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm text-white/90 hover:bg-white/[0.07] transition-colors">
                Découvrir nos réalisations <ArrowUpRight size={14} />
              </Link>
            </div>
          </FadeUp>

          <FadeUp className="mt-20">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Qu'est-ce que la création d'un site internet ?
            </h2>
            <div className="text-[var(--text-secondary)] leading-relaxed space-y-4 text-base md:text-lg">
              <p>
                Créer un site internet, c'est bien plus que mettre quelques pages en ligne. C'est concevoir la
                vitrine digitale de votre activité : son design, ses textes, sa structure, sa vitesse, son
                adaptation au mobile et sa visibilité sur Google. Un site web professionnel réussi raconte votre
                métier et donne envie de vous contacter.
              </p>
              <p>
                Chez Les Amis du Web, chaque création de site internet est réalisée sur mesure : nous partons de
                votre activité, de vos objectifs et de vos clients — jamais d'un template générique. Plusieurs de
                nos réalisations accompagnent déjà des professionnels de La Rochelle et de Charente-Maritime, ainsi
                qu'à Paris.
              </p>
            </div>
          </FadeUp>

          <FadeUp className="mt-16">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Pourquoi votre entreprise a besoin d'un site internet professionnel ?
            </h2>
            <div className="text-[var(--text-secondary)] leading-relaxed space-y-4 text-base md:text-lg">
              <p>
                Aujourd'hui, vos futurs clients vous cherchent d'abord sur Google. Sans site internet, vous êtes
                invisible ; avec un site vieillissant, vous perdez leur confiance avant même le premier contact. Un
                site internet professionnel travaille pour vous 24h/24 : il présente vos services, rassure, répond
                aux questions fréquentes et transforme les visiteurs en demandes de contact, de devis ou de
                rendez-vous.
              </p>
              <p>
                C'est particulièrement vrai pour les indépendants et les petites entreprises : restaurant, artisan,
                salon de coiffure, cabinet médical, chauffeur… Découvrez{" "}
                <Link to="/#services" className="text-[var(--accent-cyan)] hover:underline">
                  tout ce qu'un site peut apporter à votre activité
                </Link>
                .
              </p>
            </div>
          </FadeUp>

          <FadeUp className="mt-16">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Combien coûte la création d'un site internet ?
            </h2>
            <div className="text-[var(--text-secondary)] leading-relaxed space-y-4 text-base md:text-lg">
              <p>
                Les prix du marché varient énormément : de quelques centaines d'euros pour un template basique à
                plusieurs dizaines de milliers d'euros en agence traditionnelle. Chez Les Amis du Web, nous avons
                choisi la transparence : <strong className="text-white">850 € pour la création complète</strong> de
                votre site sur mesure, puis <strong className="text-white">65 € par mois</strong> pour
                l'hébergement, l'entretien et vos modifications. Le détail est présenté sur{" "}
                <Link to="/#tarifs" className="text-[var(--accent-cyan)] hover:underline">
                  notre page tarifs
                </Link>
                .
              </p>
            </div>
          </FadeUp>

          <FadeUp className="mt-16">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Combien de temps faut-il pour créer un site ?
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed text-base md:text-lg">
              Comptez quelques jours pour recevoir votre aperçu gratuit, puis 2 à 4 semaines pour le développement
              complet une fois l'aperçu validé. Le délai dépend du nombre de pages, des fonctionnalités souhaitées
              et de la rapidité avec laquelle vous nous transmettez vos contenus (photos, textes, logo).
            </p>
          </FadeUp>

          <FadeUp className="mt-16">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Site vitrine ou site sur mesure ?
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed text-base md:text-lg">
              Un site vitrine présente votre activité et vos coordonnées ; un site sur mesure va plus loin en
              adaptant chaque élément — design, parcours, fonctionnalités — à votre métier et à vos clients. Notre
              conviction : même une création de site vitrine mérite un vrai travail sur mesure. Un restaurant
              gastronomique, un salon de coiffure et une entreprise de rénovation n'ont ni les mêmes clients, ni les
              mêmes codes visuels — leur site web ne devrait jamais se ressembler.
            </p>
          </FadeUp>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 relative mt-20">
          <SectionHead
            label="Les fondations"
            title={
              <>
                Les 4 piliers d'un site internet <span className="text-gradient">réussi.</span>
              </>
            }
          />
          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="card-surface rounded-2xl p-7"
              >
                <p.icon size={20} className="text-[var(--accent-cyan)] mb-4" />
                <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">{p.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm md:text-base mt-2.5 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
          <FadeUp className="mt-8">
            <p className="text-[var(--text-secondary)] text-base leading-relaxed">
              Pour aller plus loin sur la visibilité Google, consultez{" "}
              <Link to="/#seo" className="text-[var(--accent-cyan)] hover:underline">
                notre approche du référencement naturel
              </Link>
              .
            </p>
          </FadeUp>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-10 relative mt-20">
          <FadeUp>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">
              Quelles fonctionnalités peut-on ajouter ?
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed text-base md:text-lg mb-7">
              Chaque activité a ses besoins. Voici quelques fonctionnalités que nous intégrons régulièrement lors de
              la création d'un site web :
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm md:text-base text-white/85">
                  <Check size={15} className="text-[var(--accent-cyan)] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp className="mt-16">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-7">
              Comment se déroule un projet avec Les Amis du Web ?
            </h2>
            <div className="flex flex-col gap-4">
              {STEPS.map((s) => (
                <div key={s.num} className={`rounded-2xl p-5 flex items-start gap-5 ${s.num === "02" ? "border border-violet-500/40 bg-gradient-to-r from-blue-600/[0.12] via-violet-600/[0.1] to-transparent" : "card-surface"}`}>
                  <span className="font-display font-extrabold text-xl text-gradient flex-shrink-0">{s.num}</span>
                  <p className="text-[var(--text-secondary)] text-sm md:text-base leading-relaxed pt-0.5">{s.text}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp className="mt-16">
            <div className="relative rounded-3xl border border-white/[0.09] bg-gradient-to-r from-blue-600/[0.1] via-violet-600/[0.08] to-transparent p-8 md:p-10 overflow-hidden" data-testid="pillar-apercu-section">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-[90px]" />
              <div className="relative">
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                  Un premier aperçu personnalisé, <span className="text-gradient">gratuitement et sans engagement.</span>
                </h2>
                <p className="text-[var(--text-secondary)] mt-4 leading-relaxed max-w-2xl">
                  Nous faisons ce métier par passion. Nous préférons vous montrer concrètement ce que nous pouvons
                  créer plutôt que simplement vous promettre un résultat. Décrivez-nous votre activité : nous
                  réalisons gratuitement un aperçu de votre futur site internet, et vous décidez ensuite — en toute
                  liberté.
                </p>
                <Link to="/demande" className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white mt-7" data-testid="pillar-cta-apercu">
                  Demander mon aperçu gratuit <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 relative mt-20">
          <FadeUp>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-3">
              Nos créations de sites internet <span className="text-gradient">sur mesure.</span>
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed mb-10 max-w-2xl">
              La meilleure preuve reste nos réalisations : six sites web professionnels, six univers totalement
              différents.
            </p>
          </FadeUp>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {PROJECTS.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link to={`/realisations/${p.slug}`} className="group block rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0b0c1c]" data-testid={`pillar-project-${p.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.alt} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[var(--accent-cyan)] mb-1">{p.category}</p>
                    <p className="font-display font-bold text-sm md:text-base">{p.shortName}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-10 relative mt-20">
          <FadeUp>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-8">
              Questions fréquentes sur la création de site internet
            </h2>
            <Accordion type="single" collapsible className="flex flex-col gap-3" data-testid="pillar-faq">
              {FAQ_SEO.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="card-surface rounded-2xl px-6 border-b-0">
                  <AccordionTrigger className="text-left font-display font-bold text-base md:text-lg hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--text-secondary)] text-sm md:text-base leading-relaxed pb-6">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>

          <FadeUp className="mt-16 text-center">
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
              Prêt à créer votre <span className="text-gradient">site internet ?</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 leading-relaxed">
              Parlez-nous de votre projet : nous vous répondons rapidement avec un aperçu gratuit et personnalisé.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-7">
              <Link to="/demande" className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white" data-testid="pillar-cta-final">
                Créer votre site internet <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </main>
      <Footer />
    </div>
  );
}
